import { ShaderModule, ShaderRenderContext } from './index';

/**
 * Synaptic Decay Shader Mode
 *
 * Simulates Marcus Ward's deteriorating neural pathways, optic nerve burnout,
 * and chronognostic synaptic collapse.
 * Features:
 * - CRT Phosphor raster scanlines with sweeping refresh beam and decay trails
 * - Neurological action potential pulse waveform with erratic EEG fibrillation spikes
 * - Optic nerve static bursts with horizontal noise slicing and chromatic displacement
 * - Phosphor amber (#ffb700) and retinal bleed crimson (#ff2a4b) color palette
 */
export class SynapticDecayShader implements ShaderModule {
  readonly name = 'synaptic_decay' as const;

  // Pseudo-random hash for noise slivers
  private hash(n: number): number {
    const s = Math.sin(n * 23.41) * 31415.9265;
    return s - Math.floor(s);
  }

  render(context: ShaderRenderContext): void {
    const { ctx, width, height, time, uniforms, reducedMotion } = context;
    const { speed, intensity, distortion, gridDensity } = uniforms;

    const animSpeed = reducedMotion ? speed * 0.3 : speed;
    const t = time * animSpeed;

    // 1. Dark Decaying Void Layer
    ctx.fillStyle = '#05070a';
    ctx.fillRect(0, 0, width, height);

    // Subtle phosphor green/amber ambient glow
    const ambGrad = ctx.createRadialGradient(width * 0.5, height * 0.65, 30, width * 0.5, height * 0.5, Math.max(width, height) * 0.7);
    ambGrad.addColorStop(0, 'rgba(25, 20, 10, 0.45)');
    ambGrad.addColorStop(1, 'rgba(5, 7, 10, 0.96)');
    ctx.fillStyle = ambGrad;
    ctx.fillRect(0, 0, width, height);

    ctx.save();

    // 2. CRT Phosphor Scanlines
    // Raster line spacing controlled by gridDensity
    const scanlineSpacing = Math.max(3, Math.min(8, Math.round(90 / Math.max(10, gridDensity))));
    const scanlineAlpha = 0.08 + intensity * 0.12;

    ctx.fillStyle = `rgba(0, 0, 0, ${scanlineAlpha})`;
    for (let y = 0; y < height; y += scanlineSpacing * 2) {
      ctx.fillRect(0, y, width, scanlineSpacing);
    }

    // Sweeping Phosphor Refresh Beam
    const sweepY = (time * 160 * animSpeed) % height;
    const beamHeight = 45 + intensity * 60;
    const beamGrad = ctx.createLinearGradient(0, sweepY - beamHeight, 0, sweepY);
    beamGrad.addColorStop(0, 'rgba(255, 183, 0, 0.0)');
    beamGrad.addColorStop(0.7, `rgba(255, 183, 0, ${0.05 + intensity * 0.09})`);
    beamGrad.addColorStop(1.0, `rgba(255, 183, 0, ${0.12 + intensity * 0.18})`);

    ctx.fillStyle = beamGrad;
    ctx.fillRect(0, Math.max(0, sweepY - beamHeight), width, beamHeight);

    // 3. Neurological Action Potential Pulse Waveform (EEG)
    // Runs across the lower-mid quadrant
    const baselineY = height * 0.70;
    const eegStep = Math.max(2, Math.round(width / 320));

    // Propagating action potential spikes
    const spikeCenter1 = ((t * 220) % (width + 200)) - 100;
    const spikeCenter2 = (((t * 180) + width * 0.55) % (width + 200)) - 100;

    // Draw secondary EEG glow trace
    ctx.strokeStyle = `rgba(255, 42, 75, ${0.25 + distortion * 0.45})`;
    ctx.lineWidth = 2.5;
    ctx.beginPath();

    let started = false;
    for (let x = 0; x <= width; x += eegStep) {
      // Synaptic fibrillation tremor
      const tremor = Math.sin(x * 0.06 - t * 9.0) * (3 + intensity * 8) * (reducedMotion ? 0.3 : 1.0);

      // QRS-like action potential depolarization spike
      let spike = 0;
      const d1 = Math.abs(x - spikeCenter1);
      const d2 = Math.abs(x - spikeCenter2);

      if (d1 < 70) {
        const normDist1 = (x - spikeCenter1) / 35;
        spike += Math.exp(-normDist1 * normDist1) * (-55 - distortion * 75) * Math.sin(normDist1 * Math.PI * 1.5);
      }
      if (d2 < 60 && distortion > 0.15) {
        const normDist2 = (x - spikeCenter2) / 30;
        spike += Math.exp(-normDist2 * normDist2) * (45 + distortion * 60) * Math.sin(normDist2 * Math.PI * 1.5);
      }

      const py = baselineY + tremor + spike;

      if (!started) {
        ctx.moveTo(x, py);
        started = true;
      } else {
        ctx.lineTo(x, py);
      }
    }
    ctx.stroke();

    // Primary bright amber EEG trace
    ctx.strokeStyle = `rgba(255, 183, 0, ${0.65 + intensity * 0.35})`;
    ctx.lineWidth = 1.2;
    ctx.stroke(); // Re-stroke path in crisp amber

    // 4. Optic Nerve Static Bursts
    // Stochastic glitch trigger: fires periodic retinal bursts
    const burstCycle = Math.sin(t * 3.1) * Math.cos(t * 2.1);
    const burstThreshold = 0.58 - distortion * 0.32;
    const isBursting = burstCycle > burstThreshold && !reducedMotion;

    if (isBursting) {
      const burstIntensity = (burstCycle - burstThreshold) / (1 - burstThreshold);
      const sliceCount = Math.floor(4 + distortion * 12);

      for (let s = 0; s < sliceCount; s++) {
        const seed = s * 37.17 + Math.floor(t * 15) * 11.31;
        const sliceY = this.hash(seed) * height;
        const sliceH = 2 + this.hash(seed + 1) * 16;
        const sliceW = (0.2 + this.hash(seed + 2) * 0.8) * width;
        const sliceX = (this.hash(seed + 3) * 0.5) * width;
        const shiftX = (this.hash(seed + 4) - 0.5) * (18 + distortion * 42);

        // Chromatic split static slice
        ctx.fillStyle = `rgba(255, 42, 75, ${0.15 + burstIntensity * 0.35})`;
        ctx.fillRect(sliceX + shiftX - 4, sliceY, sliceW, sliceH);

        ctx.fillStyle = `rgba(0, 243, 255, ${0.12 + burstIntensity * 0.28})`;
        ctx.fillRect(sliceX + shiftX + 4, sliceY, sliceW, sliceH);

        ctx.fillStyle = `rgba(255, 183, 0, ${0.20 + burstIntensity * 0.45})`;
        ctx.fillRect(sliceX + shiftX, sliceY, sliceW, sliceH * 0.6);
      }

      // Optic static snow flecks (stipple noise)
      const fleckCount = Math.floor(25 + distortion * 65);
      ctx.fillStyle = `rgba(255, 235, 180, ${0.4 + burstIntensity * 0.6})`;
      for (let f = 0; f < fleckCount; f++) {
        const fx = this.hash(f * 17.1 + t * 40) * width;
        const fy = this.hash(f * 43.7 + t * 40) * height;
        ctx.fillRect(fx, fy, 1.5, 1.5);
      }
    }

    ctx.restore();
  }
}
